<template>
  <Modal :style="computedWindowSize"
         :visible="visible"
         class="p-fluid"
         @close="close">
    <template v-slot:header>
      <div class="p-dialog-title">
        {{ $t('Address') }}
      </div>
    </template>

    <form autocomplete="off">
      <!--      <Spinner v-if="dataIsSending"/>-->
      <div class="p-formgrid p-grid p-pt-3">
        <div class="p-field p-col-12 p-sm-12 p-md-12 p-lg-12 p-mb-5">
          <div class="p-float-label">
            <InputText id="address"
                       :class="{'p-invalid': submitted && required && !addressData.address}"
                       v-model.trim="addressData.address"
                       autocomplete="off"
                       name="addr_line1" />
            <label for="address">{{ $t('Address') }}<span v-if="required" class="warning-color">*</span></label>
          </div>
          <small v-if="submitted && required && !addressData.address" class="p-invalid">{{ $t('Required field') }}</small>
        </div>

        <div class="p-field p-col-12 p-sm-12 p-md-12 p-lg-12 p-mb-5">
          <div class="p-float-label">
            <InputText id="address2"
                       v-model.trim="addressData.address2"
                       autocomplete="off"
                       name="addr_line2" />
            <label for="address2">{{ $t('Address') }} 2</label>
          </div>
        </div>

        <div class="p-field p-col-12 p-sm-12 p-md-12 p-lg-12 p-mb-5">
          <div class="p-float-label">
            <InputText id="city"
                       :class="{'p-invalid': submitted && required && !addressData.city}"
                       v-model.trim="addressData.city"
                       autocomplete="off"
                       name="addr_city" />
            <label for="city">{{ $t('City') }}<span v-if="required" class="warning-color">*</span></label>
          </div>
          <small v-if="submitted && required && !addressData.city" class="p-invalid">{{ $t('Required field') }}</small>
        </div>

        <div class="p-field p-col-12 p-sm-12 p-md-7 p-lg-7 p-mb-5">
          <div class="p-float-label">
            <InputText id="state"
                       v-model.trim="addressData.state"
                       autocomplete="off"
                       name="addr_state" />
            <label for="state">{{ $t('State') }}</label>
          </div>
        </div>

        <div class="p-field p-col-12 p-sm-12 p-md-5 p-lg-5 p-mb-5">
          <div class="p-float-label">
            <InputText id="postcode"
                       :class="{'p-invalid': submitted && required && !addressData.postcode}"
                       v-model.trim="addressData.postcode"
                       autocomplete="off"
                       name="addr_postcode" />
            <label for="postcode">{{ $t('Postcode') }}<span v-if="required" class="warning-color">*</span></label>
          </div>
          <small v-if="submitted && required && !addressData.postcode" class="p-invalid">{{ $t('Required field') }}</small>
        </div>

        <div class="p-field p-col-12 p-sm-12 p-md-12 p-lg-12">
          <CountryDropdown v-model="selectedCountry"
                           :countryId="addressData.country_id"
                           :resetOnClose="true"
                           v-model:isInvalid="countryIsInvalid"
                           :submitted="submitted"
                           :required="required"
                           :visible="visible"
                           :showClear="!required"
                           inputId="account-address-country-dropdown"
                           label="Country"/>
        </div>

      </div>
    </form>
    <template #footer>
      <Button :label="$t('Cancel')"
              icon="pi pi-times"
              class="p-button-text"
              @click="close"/>
      <!--              :disabled="dataIsSending"-->
      <Button :label="$t('Save')"
              icon="pi pi-check"
              class="p-button-text"
              @click="saveItem"/>
    </template>
  </Modal>
</template>

<script>
import httpClient from '@/services/http.services'
import showErrorsMixins from '@/mixins/showErrorsMixins'
import CountryDropdown from '@/components/CountryDropdown.vue'

export default {
  name: 'AccountAddressModal',

  components: {
    CountryDropdown,
  },

  mixins: [
    showErrorsMixins,
  ],

  emits: [
    'close',
    'update-item',
  ],

  props: {
    item: {
      type: Object,
      default: null,
    },

    visible: {
      type: Boolean,
      default: false,
    },

    required: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      countryIsInvalid: false,
      countriesDataIsLoading: false,
      submitted: false,
      addressData: {},
      countries: [],
      selectedCountry: null,
    }
  },

  watch: {
    visible: {
      immediate: true,

      async handler(isVisible) {
        if (!isVisible) {
          this.resetModal()
          return
        }

        await this.initializeModal()
      },
    },
  },

  methods: {
    async initializeModal() {
      this.submitted = false
      this.countryIsInvalid = false
      this.addressData = this.item ? { ...this.item } : {}

      await this.getCountries()

      const countryId =
          this.addressData.country_id
          ?? this.addressData.country?.id
          ?? null

      const countryById = countryId
          ? this.countries.find(
          country => Number(country.id) === Number(countryId)
      ) ?? null
          : null

      const countryByName =
          !countryById && this.addressData.country?.name
              ? this.countries.find(
              country => country.name === this.addressData.country.name
          ) ?? null
              : null

      const defaultCountry =
          this.countries.find(country => Number(country.is_default)) ?? null

      this.selectedCountry =
          countryById
          ?? countryByName
          ?? defaultCountry
    },

    async getCountries() {
      if (
          this.countriesDataIsLoading
          || this.countries.length
          || !this.$store.getters.userCanGetData
      ) {
        return
      }

      this.countriesDataIsLoading = true

      try {
        // const { data, status } = await httpClient('country')
        const { data, status } = await httpClient('country/modal-countries')

        if (status === 200 && Array.isArray(data)) {
          this.countries = data
        } else {
          this.countries = []
          this.showUnknownError()
        }
      } catch (err) {
        this.showError(err)
      } finally {
        this.countriesDataIsLoading = false
      }
    },

    saveItem() {
      this.submitted = true

      if (
          !this.addressData
          || (
              this.required
              && (
                  !this.addressData.address
                  || !this.addressData.city
                  || !this.addressData.postcode
              )
          )
          || !this.selectedCountry
          || this.countryIsInvalid
      ) {
        return
      }

      this.$emit('update-item', {
        address: this.addressData.address ?? null,
        address2: this.addressData.address2 ?? null,
        city: this.addressData.city ?? null,
        state: this.addressData.state ?? null,
        postcode: this.addressData.postcode ?? null,
        country_id: Number(this.selectedCountry.id),
        country: this.selectedCountry,
        country_name: this.selectedCountry.name_en,
      })

      this.close()
    },

    resetModal() {
      this.submitted = false
      this.countryIsInvalid = false
      this.selectedCountry = null
      this.addressData = {}
    },

    close() {
      this.$emit('close')
      this.resetModal()
    },
  },

  computed: {
    computedWindowSize() {
      if (
          this.$store.state.mobileLayout
          && this.$store.state.tabletLayout
      ) {
        return {
          position: 'absolute',
          top: 0,
          width: '95%',
          maxWidth: '400px',
          marginTop: '10px',
        }
      }

      if (
          !this.$store.state.mobileLayout
          && this.$store.state.tabletLayout
      ) {
        return {
          position: 'absolute',
          top: 0,
          width: '400px',
          marginTop: '25px',
        }
      }

      return {
        position: 'absolute',
        top: 0,
        width: '400px',
        marginTop: '50px',
      }
    },
  },
}
</script>